/**
 * Колонка «Вкладення» для DataTable-списків документів (2026-08-23, рішення
 * власника: скрізь, де документ має вкладення, список показує колонку).
 * Для InfiniteDocGrid — опція `attachmentsType` у useInfiniteGridDatasource
 * (той самий батч /attachments/flags), тут — DataTable-варіант по rows.
 *
 *   const att = useAttachmentFlags('doc.advance_reports', rows);
 *   <DataTable columns={[...cols, ...att.column]} ... />
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { attachments } from '@/api/attachments';
import type { ColumnDef } from '@/shared/components/DataTable';
import { AttachmentIcon } from '@/shared/components/StatusIcons';

export function useAttachmentFlags<T extends { id: string }>(
  documentType: string, rows: T[],
): { column: ColumnDef<T>[]; has: (row: T) => boolean } {
  const ids = useMemo(() => rows.map((r) => r.id), [rows]);
  const q = useQuery({
    queryKey: ['attachment-flags', documentType, ids],
    queryFn: () => attachments.flags(documentType, ids),
    enabled: ids.length > 0,
    staleTime: 30_000,
  });
  const flags = q.data ?? {};
  const has = (row: T) => !!flags[row.id];
  const column = useMemo<ColumnDef<T>[]>(() => [{
    key: '_att', header: 'Вкладення', width: 100, align: 'center',
    sortValue: (r: T) => (flags[r.id] ? 1 : 0),
    cell: (r: T) => <AttachmentIcon has={!!flags[r.id]} />,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }], [q.data]);
  return { column, has };
}
